import { useLayoutEffect, useMemo, useRef, useState } from "react"
import { Table } from "components/ui/tables/Table/Table"
import { ModelNumbersColumns } from "../../TableModelNumbersColumns"
import { resolveColumnWidths } from "../../columnWidths.helper"
import type { TModelNumberTableRow } from "types/modelNumbers.type"

type Props = {
	data: TModelNumberTableRow[]
	onRow: (record: TModelNumberTableRow) => { onClick: () => void }
	className?: string
	scrollY?: number
}

/**
 * One category's table, rendering only the rows on screen.
 *
 * Every table on the page goes through this, not just the big "Without category" one.
 * Only that one has a measurable cost to reclaim, but a page that renders some of its
 * tables as `tr`/`td` and others as `div` is worse than either choice made consistently:
 * the styling, the e2e locators and anything else reading the DOM would each need two
 * rules for one page.
 *
 * Widths are measured rather than hardcoded because antd needs numeric column widths and
 * a numeric `scroll.x` to use its virtual body at all, and a fixed pixel total would
 * either overflow the page or leave dead space beside the last column. Measuring per
 * instance is also what lets the nested tables — which sit further in, behind two more
 * levels of collapse padding — size themselves correctly.
 *
 * The measurement runs in a layout effect, so the first paint already has real widths and
 * the table never flashes at the wrong size.
 */
export const ModelNumbersCategoryTable = ({ data, onRow, className, scrollY = 600 }: Props) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const [containerWidth, setContainerWidth] = useState(0)

	useLayoutEffect(() => {
		const element = containerRef.current
		if (!element) return

		const measure = () => setContainerWidth(element.clientWidth)
		measure()

		const observer = new ResizeObserver(measure)
		observer.observe(element)
		return () => observer.disconnect()
	}, [])

	const { columns, scrollX } = useMemo(() => {
		if (!containerWidth) return { columns: ModelNumbersColumns(), scrollX: undefined }

		const { widths, total } = resolveColumnWidths(containerWidth)
		return { columns: ModelNumbersColumns(widths), scrollX: total }
	}, [containerWidth])

	return (
		<div ref={containerRef}>
			<Table
				columns={columns}
				data={data}
				scrollY={scrollY}
				showHeader={false}
				className={className}
				pagination={false}
				onRow={onRow}
				virtual={Boolean(containerWidth)}
				scrollX={scrollX}
			/>
		</div>
	)
}
